import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Heart, Calendar, Gauge, Fuel, Settings, Star, Eye, ChevronLeft, ChevronRight, Car } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  manufactureYear: number;
  modelYear: number;
  color: string;
  transmission: string;
  isZeroKm: boolean;
  store?: string;
  statistics?: {
    views?: number;
    favorites?: number;
  };
  optionals?: string[];
}

interface VehicleCardProps {
  vehicle: Vehicle;
  whatsappUrl?: string; // Made optional since we'll use vehicle's store
}

export const VehicleCard = ({ vehicle, whatsappUrl }: VehicleCardProps) => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const { availableStores, selectedStore } = useStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  // Use a fallback slug immediately to avoid placeholder flash
  const [vehicleSlug, setVehicleSlug] = useState<string>(() => {
    // Generate a temporary slug immediately to avoid placeholder
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });
  // Initialize image states - start with true to show content immediately
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true); // Start as true to render immediately

  // Ensure colors are available immediately with fallbacks
  const buttonColor = colors?.button || '#3b82f6';
  const primaryColor = colors?.primary || '#3b82f6';
  const secondaryColor = colors?.secondary || '#64748b';
  const titleColor = colors?.title || '#1e293b';
  const textColor = colors?.text || '#64748b';

  // Get store info for this vehicle (based on vehicle.store field) - use memo to avoid recalculation
  const storeId = vehicle.store;
  const vehicleStore = useMemo(() => {
    if (!config?.stores) return null;
    return storeId
      ? config.stores.find((s: any) => s._id === storeId) 
      : (selectedStore || config.stores[0]);
  }, [config?.stores, storeId, selectedStore]);
  const showStoreBadge = availableStores.length > 1 && vehicleStore && storeId;

  // Check if vehicle is favorited on mount and generate proper slug - non-blocking
  useEffect(() => {
    // Run async operations without blocking render
    const init = async () => {
      try {
        // Check favorites - non-blocking
        secureGetItem('favorites').then(favorites => {
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(vehicle.id));
          }
        }).catch(() => {}); // Silently fail
        
        // Generate proper slug - non-blocking
        generateVehicleSlug(vehicle).then(slug => {
          setVehicleSlug(slug);
        }).catch(() => {}); // Silently fail - keep temporary slug
      } catch (error) {
        // Silently fail - card will still render with temporary slug
      }
    };
    init();
  }, [vehicle.id]);

  // Handle favorite toggle
  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation when clicking on favorite button
    e.stopPropagation();

    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      // Remove from favorites
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      // Add to favorites
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
    }

    await secureSetItem('favorites', updatedFavorites);

    // Dispatch custom event to update other components
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const nextImage = () => {
    if (isImageLoading) return; // Block if loading
    const nextIdx = currentImageIndex === vehicle.photos.length - 1 ? 0 : currentImageIndex + 1;
    preloadImage(nextIdx);
  };

  const prevImage = () => {
    if (isImageLoading) return; // Block if loading
    const prevIdx = currentImageIndex === 0 ? vehicle.photos.length - 1 : currentImageIndex - 1;
    preloadImage(prevIdx);
  };

  // Preload image before changing
  const preloadImage = (index: number) => {
    setIsImageLoading(true);
    setImageLoaded(false);
    const img = new Image();
    
    // Handle errors silently to avoid console errors
    img.onerror = () => {
      // Silently handle error - image doesn't exist or failed to load
      setCurrentImageIndex(index);
      setIsImageLoading(false);
      setImageLoaded(true);
      // Don't log to console to avoid noise
    };
    
    img.onload = () => {
      setCurrentImageIndex(index);
      setIsImageLoading(false);
      setImageLoaded(true);
    };
    
    img.src = vehicle.photos[index];
  };

  // Preload first image immediately - run synchronously without blocking
  useEffect(() => {
    if (vehicle.photos && vehicle.photos.length > 0 && vehicle.photos[0]) {
      const firstImageUrl = vehicle.photos[0];
      
      // Force immediate load - create image object synchronously
      const img = new Image();
      
      // Handle errors silently to avoid console errors
      img.onerror = () => {
        // Silently handle error - image doesn't exist or failed to load
        // Don't log to console to avoid noise
      };
      
      img.src = firstImageUrl;
      
      // Also add preload link for browser optimization
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = firstImageUrl;
      link.setAttribute('fetchpriority', 'high');
      
      // Handle preload errors silently
      link.onerror = () => {
        // Silently handle preload error
        if (link.parentNode) {
          link.remove();
        }
      };
      
      document.head.appendChild(link);
      
      // Clean up link after image loads or after timeout
      const cleanup = () => {
        setTimeout(() => {
          if (link.parentNode) {
            link.remove();
          }
        }, 2000);
      };
      
      if (img.complete) {
        cleanup();
      } else {
        img.onload = cleanup;
        img.onerror = () => {
          cleanup();
          // Error already handled above, just cleanup
        };
      }
    }
  }, [vehicle.photos]);

  // Handle image load complete
  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoaded(true);
  };

  const handleWhatsApp = () => {
    const message = `Olá! Tenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}\nAno: ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de mais informações.`;
    // Use vehicle's store whatsapp URL, fallback to prop if provided
    const storeWhatsappUrl = vehicleStore?.whatsapp?.ios || whatsappUrl;
    if (storeWhatsappUrl) {
      const url = `${storeWhatsappUrl}&text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  };

  const handleImageNavigation = (e: React.MouseEvent, direction: 'prev' | 'next') => {
    e.preventDefault();
    e.stopPropagation();
    if (direction === 'prev') {
      prevImage();
    } else {
      nextImage();
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Block navigation if image is loading
    if (isImageLoading) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Link 
      to={`/estoque/${vehicleSlug}`} 
      className="block h-full" 
      onClick={handleCardClick} 
      style={{ 
        visibility: 'visible', 
        opacity: 1,
        // Force immediate rendering
        contentVisibility: 'visible',
        containIntrinsicSize: 'auto'
      }}
    >
      <Card 
        className="group overflow-hidden transition-all duration-300 transform flex flex-col h-full cursor-pointer border border-transparent bg-background"
        style={{
          // Apply all styles immediately
          borderColor: 'transparent',
          backgroundColor: 'hsl(var(--background))',
          // Force immediate paint
          willChange: 'transform, border-color'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = buttonColor;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'transparent';
        }}
      >
        {/* Image Gallery - Larger and more impactful */}
        <div className="relative aspect-[4/2.5] overflow-hidden flex-shrink-0 bg-muted">
          {/* Loading Spinner - only show when actively loading */}
          {isImageLoading && !imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 backdrop-blur-[2px] z-20">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin opacity-70"></div>
            </div>
          )}

          {vehicle.photos && vehicle.photos.length > 0 && vehicle.photos[currentImageIndex] ? (
            <>
              {/* Optimized responsive image */}
              {(() => {
                const imageUrl = vehicle.photos[currentImageIndex];
                // Card width is approximately 260px (w-[260px]), aspect ratio 4:2.5
                const optimized = generateResponsiveImageUrls(imageUrl, 260, 162);
                const loadingStrategy = getImageLoadingStrategy(currentImageIndex === 0, currentImageIndex === 0);
                
                return (
                  <img
                    src={optimized.src}
                    srcSet={optimized.srcSet}
                    sizes={optimized.sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 260px'}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                    loading={loadingStrategy.loading}
                    fetchpriority={loadingStrategy.fetchpriority}
                    decoding="async"
                    width={optimized.width}
                    height={optimized.height}
                    style={{ 
                      opacity: 1,
                      visibility: 'visible',
                      display: 'block',
                      contentVisibility: 'visible'
                    }}
                    onLoad={handleImageLoad}
                    onError={(e) => {
                      // Silently handle error - show placeholder instead of broken image
                      e.currentTarget.style.display = 'none';
                      setIsImageLoading(false);
                      setImageLoaded(true);
                    }}
                  />
                );
              })()}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
              <Car className="h-12 w-12 opacity-50" />
            </div>
          )}

          {/* Navigation Arrows - Modern Circular Style */}
          {vehicle.photos && vehicle.photos.length > 1 && (
            <>
              <button
                onClick={(e) => handleImageNavigation(e, 'prev')}
                disabled={isImageLoading}
                className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white text-gray-800 rounded-full p-2 transition-all duration-300 z-10 shadow-lg hover:shadow-xl ${isImageLoading ? 'cursor-not-allowed opacity-30' : 'opacity-0 group-hover:opacity-100 hover:scale-110'}`}
                aria-label={`Ver foto anterior de ${vehicle.brand} ${vehicle.model}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => handleImageNavigation(e, 'next')}
                disabled={isImageLoading}
                className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white text-gray-800 rounded-full p-2 transition-all duration-300 z-10 shadow-lg hover:shadow-xl ${isImageLoading ? 'cursor-not-allowed opacity-30' : 'opacity-0 group-hover:opacity-100 hover:scale-110'}`}
                aria-label={`Ver próxima foto de ${vehicle.brand} ${vehicle.model}`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Gradient Overlay - Bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Favorite Button - Top Right */}
          <button
            onClick={toggleFavorite}
            className="absolute top-3 right-3 p-2 bg-white/95 backdrop-blur-sm rounded-full hover:bg-white transition-all z-20 hover:scale-110"
            title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            <Heart
              className={`h-4 w-4 transition-all ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
            />
          </button>

          {/* Image Counter - Bottom Left */}
          {vehicle.photos && vehicle.photos.length > 1 && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1">
              {vehicle.photos.map((_, idx) => (
                <div 
                  key={idx}
                  className={`h-1 rounded-full transition-all ${
                    idx === currentImageIndex ? 'w-4 bg-white' : 'w-1 bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Zero KM Badge - Top Left */}
          {vehicle.isZeroKm && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-green-600/95 backdrop-blur-md text-white text-xs font-medium px-3 py-1">
                Zero KM
              </Badge>
            </div>
          )}
        </div>

        {/* Compact Info Section */}
        <CardContent className="p-4 flex flex-col" style={{ padding: '1rem' }}>
          {/* Title and Version */}
          <div className="mb-3 flex-shrink-0" style={{ marginBottom: '0.75rem' }}>
            <h3 
              className="font-bold text-base text-foreground line-clamp-1 group-hover:text-primary transition-colors h-6"
              style={{ 
                color: 'hsl(var(--foreground))',
                fontSize: '1rem',
                fontWeight: 700,
                height: '1.5rem',
                lineHeight: '1.5rem'
              }}
            >
              {vehicle.brand} {vehicle.model}
            </h3>
            <p 
              className="text-xs text-muted-foreground line-clamp-1 mb-2 h-4"
              style={{
                color: 'hsl(var(--muted-foreground))',
                fontSize: '0.75rem',
                height: '1rem',
                lineHeight: '1rem',
                marginBottom: '0.5rem'
              }}
            >
              {vehicle.version}
            </p>
            {/* Price */}
            <div className="flex items-baseline gap-0.5">
              <span 
                className="text-xl font-bold" 
                style={{ 
                  color: titleColor,
                  fontSize: '1.25rem',
                  fontWeight: 700
                }}
              >
                R$ {formatPrice(vehicle.price).replace('R$', '').trim()}
              </span>
            </div>
          </div>

          {/* Compact Details - Single Row */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground border-t border-border/50 pt-3 flex-shrink-0 h-8">
            {vehicle.modelYear && (
              <span className="flex items-center gap-1 whitespace-nowrap">
                <Calendar className="h-3 w-3" />
                {vehicle.manufactureYear}/{vehicle.modelYear}
              </span>
            )}
            {vehicle.mileage !== undefined && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Gauge className="h-3 w-3" />
                  {vehicle.mileage === 0 ? '0km' : `${(vehicle.mileage / 1000).toFixed(3)}k`}
                </span>
              </>
            )}
          </div>

          {/* Hover CTA */}
          <div className="text-xs text-muted-foreground mt-3 opacity-0 group-hover:opacity-100 transition-opacity h-4 flex-shrink-0">
            Clique para ver detalhes →
          </div>
        </CardContent>
      </Card>
    </Link >
  );
};